/**
 * Category hub `/{cc}/{lang}/category/:l1/:l2?` (web plan W3.1), born at its
 * final URL: `/pk/en/category/fashion-and-apparel`,
 * `/pk/en/category/fashion-and-apparel/shoes-and-footwear`. Levels 1–2 only
 * (a level-3 category's chips link its level-2 parent's hub).
 *
 * Everything comes from the backend's `categoryPage` (B3.7b) through
 * `useCategoryPage`; the SEO server builds the head from the same query
 * (server/routes.mjs), so the raw HTML and the page agree:
 * - H1 / title / description from the category formula (`categoryPageHead`:
 *   "Today" and the top 3 brands only while a listed sale is live; a staff
 *   `seoOverrides` title / description wins), robots from `seo.robots`;
 * - the live sales, LIST_PAGE_SIZE a page with crawlable `?page=N` links (a
 *   page that does not exist says so and is `noindex`; the server answers it
 *   with a 404, and `?page=1` with a 301 to the bare path);
 * - on page 1: the category's intro text (B3.4), the upcoming sales,
 *   "{Category} brands in {Country} ({n})" and, on a level-1 hub, its
 *   indexable level-2 hubs;
 * - D-5: a brand, parent or sub-category hub is linked only while its own
 *   `seo.indexable` (plain text otherwise).
 *
 * An unknown category, an `l2` that is not a child of `l1` and a key no slug
 * can have are the 404 page (noindex); another spelling of the hub's path
 * (`/PK/EN/Category/Shoes`, a trailing slash) is replaced by its `urlPath`.
 * Every string comes from the catalogue (`category:page.*`, web plan W3.0).
 */
import { lazy, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { PromotionCard } from '@/components/cards';
import { CardSkeleton, ErrorState, NoPageNotice, Pagination } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useCategoryPage } from '@/hooks/useCategoryPage';
import { useImpressionRef } from '@/hooks/useImpressionTracking';
import { PageHead } from '@/components/seo/PageHead';
import { formatNumber } from '@/i18n/format';
import { useTranslate } from '@/i18n/LocaleLanguage';
import type { Translate } from '@/i18n';
import {
  categoryHubKey,
  categoryHubLabel,
  categoryHubLastPage,
  categoryHubPromotionsOn,
  categoryName,
  linkedFromHubs,
  sitePath,
  type CategoryHubPage,
} from '@/seo/categoryHubs';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { categoryPageHead } from '@/seo/pageHeads';
import { catalogPath, entityPath, parseListPage } from '@/seo/urls';
import { cleanName } from '@/seo/text';

// Lazy like every page in App.tsx, so the 404 page stays in its own chunk.
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** Plain text as paragraphs (blank lines separate them). */
function paragraphsOf(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/** A grid cell that registers the card for impression view-tracking. */
function TrackedCell({ id, children }: { id: string; children: ReactNode }) {
  const impressionRef = useImpressionRef('promotion', id);
  // The <li> is the grid item and stretches to its row; `*:h-full` passes
  // that height on to the card, so a row's cards are equal.
  return (
    <li ref={impressionRef} className="h-full *:h-full">
      {children}
    </li>
  );
}

const GRID = 'mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
const COUNT = 'text-base font-semibold text-ink-tertiary';
const SECTION_HEADING = 'text-xl font-extrabold tracking-tight text-ink';

/** The page's `?page=` against the hub's live sales. */
function hubPageOf(search: string, hub: CategoryHubPage): { page: number; ok: boolean; requested: string } {
  const param = parseListPage(search);
  const requested = param.raw ?? '';
  if (!param.valid) return { page: 1, ok: false, requested };
  return { page: param.page, ok: param.page <= categoryHubLastPage(hub), requested };
}

function paginationLabels(t: Translate) {
  return {
    previous: t('common:pagination.previous'),
    next: t('common:pagination.next'),
    page: (page: number) => t('common:pagination.page', { page }),
  };
}

export interface CategoryHubContentProps {
  hub: CategoryHubPage;
  locale?: Locale;
}

/** The hub for one category's answer. Exported so tests can render any fixture. */
export function CategoryHubContent({ hub, locale = DEFAULT_LOCALE }: CategoryHubContentProps) {
  const { search } = useLocation();
  const t = useTranslate();
  const { page, ok, requested } = hubPageOf(search, hub);
  const head = categoryPageHead(hub, { page, locale, t });
  const name = categoryName(hub.category);
  const live = categoryHubPromotionsOn(hub, page);
  const intro = page === 1 ? paragraphsOf(hub.category.description) : [];
  const subCategories = hub.subCategories.filter((sub) => sitePath(sub.urlPath));

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      {/* A page past the end or a malformed `?page=` does not exist (the server's 404). */}
      <PageHead head={head} noindex={!ok} />
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>
        {intro.length > 0 && (
          <div className="mt-3 max-w-3xl space-y-3 text-ink-secondary">
            {intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}
      </header>

      {!ok ? (
        <NoPageNotice
          basePath={hub.urlPath}
          requested={requested}
          labels={{
            noPage: (shown) => t('common:pagination.noPage', { page: shown }),
            firstPage: t('common:pagination.firstPage'),
          }}
        />
      ) : (
        <div className="mt-8 space-y-12">
          <section aria-labelledby="category-live-heading">
            <h2 id="category-live-heading" className={SECTION_HEADING}>
              {t('category:page.live')}
              {hub.livePromotions.length > 0 && (
                <span className={COUNT}>{` (${formatNumber(hub.livePromotions.length, locale)})`}</span>
              )}
            </h2>
            {live.length > 0 ? (
              <ul className={GRID}>
                {live.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Tag size={28} className="text-primary" aria-hidden="true" />
                </span>
                <p className="font-bold text-ink">{t('category:page.liveEmpty', { category: name })}</p>
                <p className="text-sm text-ink-secondary">{t('category:page.liveEmptyHint')}</p>
                {/* Nothing at all in this country (the backend answers an empty hub, never another country's data). */}
                {page === 1 && hub.brandCount === 0 && hub.upcomingPromotions.length === 0 && (
                  <>
                    <p className="text-sm text-ink-secondary">
                      {t('category:page.nothingYet', { category: name, country: locale.country })}
                    </p>
                    <Link
                      to={catalogPath(locale, '/promotions')}
                      className="text-sm font-semibold text-primary-strong hover:underline"
                    >
                      {t('category:page.browseAll', { country: locale.country })}
                    </Link>
                  </>
                )}
              </div>
            )}
            {/* Plain <a href> links to every page of live sales, for crawlers. */}
            <Pagination
              basePath={hub.urlPath}
              page={page}
              lastPage={categoryHubLastPage(hub)}
              label={t('category:page.pages')}
              labels={paginationLabels(t)}
            />
          </section>

          {page === 1 && hub.upcomingPromotions.length > 0 && (
            <section aria-labelledby="category-upcoming-heading">
              <h2 id="category-upcoming-heading" className={SECTION_HEADING}>
                {t('category:page.upcoming')}
                <span className={COUNT}>{` (${formatNumber(hub.upcomingPromotions.length, locale)})`}</span>
              </h2>
              <ul className={GRID}>
                {hub.upcomingPromotions.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            </section>
          )}

          {page === 1 && hub.brandCount > 0 && hub.brands.length > 0 && (
            <section aria-labelledby="category-brands-heading">
              <h2 id="category-brands-heading" className={SECTION_HEADING}>
                {t('category:page.brands', {
                  category: name,
                  country: locale.country,
                  count: formatNumber(hub.brandCount, locale),
                })}
              </h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {hub.brands.map((brand) => {
                  const label = cleanName(brand.name) || brand.name;
                  return (
                    <li key={brand.id}>
                      {/* D-5: a brand hub is linked from here only while it is indexable. */}
                      {linkedFromHubs(brand) ? (
                        <Link
                          to={entityPath(brand, 'brand')}
                          className="inline-flex min-h-10 items-center rounded-full border border-line px-3 py-1.5 text-sm font-semibold text-ink transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary-strong"
                        >
                          {label}
                        </Link>
                      ) : (
                        <span className="inline-flex min-h-10 items-center rounded-full border border-line-light px-3 py-1.5 text-sm text-ink-secondary">
                          {label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {page === 1 && subCategories.length > 0 && (
            <section aria-labelledby="category-subs-heading">
              <h2 id="category-subs-heading" className={SECTION_HEADING}>
                {t('category:page.subCategories', { category: name })}
              </h2>
              {/* The backend lists only the indexable level-2 hubs (D-5). */}
              <ul className="mt-4 flex flex-wrap gap-2">
                {subCategories.map((sub) => (
                  <li key={sub.id}>
                    <Link
                      to={sitePath(sub.urlPath) as string}
                      className="inline-flex min-h-10 items-center rounded-full border border-primary/40 bg-primary/15 px-3 py-1.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-primary/25"
                    >
                      {categoryHubLabel(sub)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/** Loading or a failed load: say what the page is and leave robots alone (web plan W0.2). */
function CategoryHubPending({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  const t = useTranslate();
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead title={t('category:page.loading')} keepRobots />
      {error ? (
        <ErrorState
          title={t('category:page.error')}
          titleAs="h1"
          message={error}
          onRetry={onRetry}
          retryLabel={t('category:page.retry')}
        />
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface CategoryPageProps {
  /** The route's locale: routes are generated per locale with literal segments (App.tsx). */
  locale?: Locale;
}

export default function CategoryPage({ locale = DEFAULT_LOCALE }: CategoryPageProps) {
  const { l1, l2 } = useParams();
  const { pathname, search, hash } = useLocation();
  const key = categoryHubKey(l1, l2);
  const { hub, status, error, refetch } = useCategoryPage(key, locale);

  if (status === 'not_found') return <NotFoundPage />;
  if (status !== 'ok' || !hub) return <CategoryHubPending error={status === 'error' ? error : null} onRetry={refetch} />;

  // Another spelling of the hub's path: the page itself at its one URL (the
  // SEO server answers it with one 301).
  const canonical = sitePath(hub.urlPath);
  if (canonical && pathname !== canonical) return <Navigate replace to={{ pathname: canonical, search, hash }} />;

  return <CategoryHubContent key={hub.urlPath} hub={hub} locale={locale} />;
}
